import { useEffect } from 'react';
import { StringParam, useQueryParams } from 'use-query-params';
import {
  DEFAULT_LIST_PARAMS,
  GetUrlParams,
  ListParams,
  PaginationParams,
} from '@lib/enums/urls';
import { sanitizePageParam, sanitizePerPageParam } from '@lib/utils/pagination';

const paramConfig = {
  [GetUrlParams.Page]: StringParam,
  [GetUrlParams.PerPage]: StringParam,
  [GetUrlParams.OrderingField]: StringParam,
  [GetUrlParams.OrderingDirection]: StringParam,
} as const;

const usePaginationParams = (): [ListParams, typeof setPaginationParams] => {
  const [rawParams, setPaginationParams] =
    useQueryParams<typeof paramConfig>(paramConfig);

  const cleanedParams = Object.fromEntries(
    Object.entries(rawParams).filter(([_, value]) => value !== undefined),
  ) as Partial<PaginationParams>;

  const paginationParams: ListParams = {
    ...DEFAULT_LIST_PARAMS,
    ...cleanedParams,
  };

  // Guards against a hand-edited/stale `?page=1.5` or `?per_page=abc` URL:
  // callers always get a sane value immediately, and the URL is corrected
  // (via history replace, so it doesn't add a back-button entry) to match
  // what was actually used, instead of silently diverging from it.
  const page = String(sanitizePageParam(paginationParams[GetUrlParams.Page]));
  const perPage = String(
    sanitizePerPageParam(paginationParams[GetUrlParams.PerPage]),
  );

  useEffect(() => {
    const invalidParams: Partial<PaginationParams> = {};
    if (
      rawParams[GetUrlParams.Page] !== undefined &&
      rawParams[GetUrlParams.Page] !== page
    ) {
      invalidParams[GetUrlParams.Page] = page;
    }
    if (
      rawParams[GetUrlParams.PerPage] !== undefined &&
      rawParams[GetUrlParams.PerPage] !== perPage
    ) {
      invalidParams[GetUrlParams.PerPage] = perPage;
    }
    if (Object.keys(invalidParams).length) {
      setPaginationParams(invalidParams, 'replaceIn');
    }
  }, [rawParams, page, perPage, setPaginationParams]);

  return [
    {
      ...paginationParams,
      [GetUrlParams.Page]: page,
      [GetUrlParams.PerPage]: perPage,
    },
    setPaginationParams,
  ];
};

export default usePaginationParams;
